import * as React from "react";
import { Button, Col, Row } from "react-bootstrap";

import type { ErrorObject } from "../../../../../../../../../server/types/error";
import { ConfirmButton } from "../../../../../../parts/confirm-dropdown-button";
import { TextRed } from "../../../../../../parts/font";
import { TextRow } from "../../../../../../parts/input-row";
import { ReportTitle, ReportTitleText } from "../../../../parts/report-title";
import { CpActualValueTableInput } from "./cp-actual-value-table-input";
import { PurchaseRateExtractionDefinitionNotes } from "../../../../../../parts/purchase-rate-extraction-definition-notes";
import { Job } from "../../../../../../../../../server/models/job";
import { ChangeRateValue } from "../../../../../../../../../server/models/cp2";
import { CurrentProductCorrectionValue } from "../../../../../../../../../server/types/request/report/simulation";

type Props = {
	cp: CurrentProductCorrectionValue;
	errors: ErrorObject | undefined;
	hasEditRole: boolean;
	job: Job;
	onChange: <K extends keyof ChangeRateValue>(key: K, value: ChangeRateValue[K]) => void;
	onClear: () => void;
	onSubmit: () => void;
};

export const CPSetting = React.memo(({ cp, errors, hasEditRole, job, onChange, onClear, onSubmit }: Props) => {
	const { value, reason } = cp.purchaseRate ?? {};

	const canSubmit = !!value && (!!reason || value === job.currentProductPurchaseRate);

	const disabled = !!cp._id && !!cp?.correctionFactor;

	return (
		<>
			<Row>
				<Col md={10}>
					<ReportTitle>現行品/類似品の購入率修正</ReportTitle>
				</Col>
			</Row>

			<Row style={{ marginBottom: "30px" }}>
				{/* 商品認知率修正と位置調整とのため常に表示させておく */}
				<Col md={{ span: 1 }}>
					{!!cp._id && (
						<ConfirmButton
							body="実績値修正のクリアをします。よろしいでしょうか。"
							buttonText="クリア"
							onOk={onClear}
							title="設定値のクリア"
							variant="outline-secondary"
						>
							実績設定のクリア
						</ConfirmButton>
					)}
				</Col>
				<Col>
					<ReportTitleText
						className="border"
						style={{
							color: "#C00000",
							padding: "10px",
						}}
					>
						購入率のみの変更の場合でも、下段の設定が必要です
						<br />
						➀上段の＜購入率 修正＞で入力完了した後に＜購入率 更新＞ボタンをクリック
						<br />
						②下段の＜商品認知率 修正＞で「初期値から変更しない」を選択
						<br />
						③最後に＜推定値 算出＞のボタンをクリック
					</ReportTitleText>
				</Col>
			</Row>

			<Row>
				<Col md={8}>
					{/* 現行品/類似品 の */}
					{/* 直近12か月の購入率(%) */}
					<CpActualValueTableInput
						key={cp._id?.toString()}
						disabled={disabled}
						errors={errors}
						initialValue={job.currentProductPurchaseRate ?? 0}
						onChange={onChange}
						value={value}
					/>

					<TextRow
						as="textarea"
						disabled={disabled}
						errors={errors}
						id="reason"
						name="reason"
						onChange={(value) => {
							onChange("reason", value);
						}}
						rowName="修正の内容（期間や単位等変更した詳細を記載）"
						value={reason?.toString() ?? ""}
					/>

					<div
						className="text-end"
						style={{
							marginRight: 19.2,
						}}
					>
						<TextRed>初期値から変更した場合は入力必須</TextRed>
					</div>
				</Col>
				<Col>
					<PurchaseRateExtractionDefinitionNotes assessmentType="adCopy" />
				</Col>
			</Row>

			{hasEditRole && (
				<Row>
					{/* 購入率率修正との位置調整のため常に表示させておく */}
					<Col md={{ span: 1 }}>
						{/* spacer */}
						<></>
					</Col>
					<Col>
						<Row>
							{disabled ? (
								<Col md={{ offset: 2, span: 8 }}>
									<TextRed>値を変更する場合はクリアボタンを押して下さい。</TextRed>
								</Col>
							) : (
								<Col md={{ offset: 2, span: 2 }}>
									<Button disabled={!canSubmit} onClick={onSubmit} variant="secondary">
										購入率 更新
									</Button>
								</Col>
							)}
						</Row>
					</Col>
				</Row>
			)}
		</>
	);
});
